"use client";

import {
  IoArrowBackOutline,
  IoCheckmarkOutline,
  IoChevronDownOutline,
  IoCloseOutline,
  IoCloudUploadOutline,
  IoContractOutline,
  IoCreateOutline,
  IoDocumentOutline,
  IoDownloadOutline,
  IoExpandOutline,
  IoFilterOutline,
  IoFolderOutline,
  IoGridOutline,
  IoListOutline,
  IoLockClosedOutline,
  IoLogOutOutline,
  IoMenuOutline,
  IoPrintOutline,
  IoSearchOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Main app ──────────────────────────────────────────────────────────────────
import DOCS_DATA from "../docs-data.json";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { io } from "socket.io-client";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

// ── Auth config ───────────────────────────────────────────────────────────────
// Connect to auth-service for real JWT auth, OR use PIN fallback for standalone use
const AUTH_API = process.env.NEXT_PUBLIC_AUTH_URL || "http://localhost:4001";
const GW_URL = process.env.NEXT_PUBLIC_GATEWAY_URL || "http://localhost:4000";
const USE_PIN = process.env.NEXT_PUBLIC_USE_PIN_AUTH === "true";
const PINS = { director: "0000", legal: "1111", finance: "2222", ops: "3333" };
const ROLE_META = {
  director: {
    label: "Director",
    color: "#a78bfa",
    emoji: "🛡️",
    desc: "Full access — all documents",
  },
  legal: {
    label: "Legal",
    color: "#60a5fa",
    emoji: "⚖️",
    desc: "Legal, compliance, corporate",
  },
  finance: {
    label: "Finance",
    color: "#4ade80",
    emoji: "💰",
    desc: "Finance, investor, strategy",
  },
  ops: {
    label: "Operations",
    color: "#fb923c",
    emoji: "⚙️",
    desc: "Operations, guides, manuals",
  },
};

// ── Folder config ─────────────────────────────────────────────────────────────
const FOLDERS = {
  guides: {
    label: "Guides",
    color: "#fb923c",
    icon: "📘",
    roles: ["director", "ops"],
  },
  legal: {
    label: "Legal",
    color: "#f87171",
    icon: "⚖️",
    roles: ["director", "legal"],
  },
  compliance: {
    label: "Compliance",
    color: "#60a5fa",
    icon: "🛡️",
    roles: ["director", "legal", "ops"],
  },
  corporate: {
    label: "Corporate",
    color: "#a78bfa",
    icon: "🏢",
    roles: ["director", "legal"],
  },
  finance: {
    label: "Finance",
    color: "#4ade80",
    icon: "📊",
    roles: ["director", "finance"],
  },
  investor: {
    label: "Investor",
    color: "#fbbf24",
    icon: "💼",
    roles: ["director", "finance"],
  },
  strategy: {
    label: "Strategy",
    color: "#f472b6",
    icon: "🎯",
    roles: ["director", "ops"],
  },
  operations: {
    label: "Operations",
    color: "#34d399",
    icon: "⚙️",
    roles: ["director", "ops"],
  },
  hr: { label: "HR", color: "#e879f9", icon: "👥", roles: ["director"] },
  technology: {
    label: "Technology",
    color: "#38bdf8",
    icon: "💻",
    roles: ["director", "ops"],
  },
  marketing: {
    label: "Marketing",
    color: "#f97316",
    icon: "📣",
    roles: ["director", "ops"],
  },
  sales: {
    label: "Sales",
    color: "#10b981",
    icon: "🤝",
    roles: ["director", "ops"],
  },
  brand: {
    label: "Brand",
    color: "#fb923c",
    icon: "🎨",
    roles: ["director", "ops"],
  },
  manuals: {
    label: "Manuals",
    color: "#94a3b8",
    icon: "📋",
    roles: ["director", "ops", "legal", "finance"],
  },
  playbooks: {
    label: "Playbooks",
    color: "#22c55e",
    icon: "📗",
    roles: ["director", "ops", "finance", "legal"],
  },
  profile: {
    label: "Company",
    color: "#fbbf24",
    icon: "🏢",
    roles: ["director", "legal", "finance", "ops"],
  },
};

// ── Markdown components ───────────────────────────────────────────────────────
const MD_COMPONENTS = {
  code({ node, inline, className, children, ...props }) {
    const lang = /language-(\w+)/.exec(className || "");
    return !inline && lang ? (
      <SyntaxHighlighter
        style={oneDark}
        language={lang[1]}
        PreTag="div"
        {...props}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

// ── Word count + read time ────────────────────────────────────────────────────
const readTime = (content = "") => {
  const words = content.trim().split(/\s+/).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read · ${words.toLocaleString()} words`;
};

// ── Download helper ───────────────────────────────────────────────────────────
const downloadMd = (doc) => {
  const blob = new Blob([doc.content], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.filename;
  a.click();
  URL.revokeObjectURL(url);
};

const printDoc = () => window.print();

// ── Login screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [step, setStep] = useState("roles"); // roles | pin | jwt
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const pinRef = useRef(null);

  const tryPin = () => {
    if (PINS[selected] === pin.trim()) {
      onLogin({ role: selected, name: ROLE_META[selected].label, via: "pin" });
    } else {
      setErr("Incorrect PIN");
      setPin("");
      setTimeout(() => setErr(""), 2000);
    }
  };

  const tryJwt = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${AUTH_API}/api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pass }),
      });
      const data = await res.json();
      if (data.success && data.data?.user) {
        const u = data.data.user;
        const role = ["super_admin", "org_admin"].includes(u.role)
          ? "director"
          : "ops";
        onLogin({
          role,
          name: u.name,
          email: u.email,
          token: data.data.accessToken,
          via: "jwt",
        });
      } else {
        setErr(data.message || "Login failed");
      }
    } catch {
      setErr("Cannot reach auth service — use PIN login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--bg)",
        position: "relative",
      }}
    >
      {/* ambient glow */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(ellipse 70% 60% at 50% -10%,rgba(90,40,118,.22),transparent)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(ellipse 50% 40% at 80% 80%,rgba(255,121,0,.06),transparent)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 32 }}>
          <p
            style={{
              fontSize: "2.4rem",
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: ".15em",
              background: "var(--grad-orange)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            🐙 Octopus
          </p>
          <p
            style={{
              fontSize: ".75rem",
              color: "var(--text3)",
              textTransform: "uppercase",
              letterSpacing: ".12em",
              marginTop: 4,
            }}
          >
            Business Document Library
          </p>
          <p
            style={{ fontSize: ".75rem", color: "var(--text3)", marginTop: 2 }}
          >
            Zizian (Pty) Ltd · Reg: 2021/435383/07
          </p>
        </div>

        <div
          style={{
            background: "var(--glass)",
            border: "1px solid var(--glass-border)",
            backdropFilter: "blur(24px)",
            borderRadius: 24,
            padding: 36,
          }}
        >
          {step === "roles" && (
            <>
              <p
                style={{
                  fontSize: ".8rem",
                  color: "var(--text3)",
                  marginBottom: 20,
                  textTransform: "uppercase",
                  letterSpacing: ".1em",
                }}
              >
                Select your access role
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {Object.entries(ROLE_META).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => {
                      setSelected(k);
                      setErr("");
                      setPin("");
                      setStep("pin");
                      setTimeout(() => pinRef.current?.focus(), 80);
                    }}
                    style={{
                      padding: "14px 18px",
                      borderRadius: 14,
                      border: "1px solid var(--glass-border)",
                      background: "var(--glass)",
                      color: "var(--text2)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      textAlign: "left",
                      transition: "all .2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,.07)";
                      e.currentTarget.style.color = "var(--text)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "var(--glass)";
                      e.currentTarget.style.color = "var(--text2)";
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.1rem",
                        background: `${v.color}18`,
                        flexShrink: 0,
                      }}
                    >
                      {v.emoji}
                    </div>
                    <div>
                      <p
                        style={{
                          fontWeight: 700,
                          fontSize: ".88rem",
                          color: "inherit",
                        }}
                      >
                        {v.label}
                      </p>
                      <p
                        style={{
                          fontSize: ".75rem",
                          color: "var(--text3)",
                          marginTop: 1,
                        }}
                      >
                        {v.desc}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              {!USE_PIN && (
                <button
                  onClick={() => setStep("jwt")}
                  style={{
                    marginTop: 16,
                    width: "100%",
                    padding: "10px",
                    borderRadius: 12,
                    border: "1px solid var(--glass-border)",
                    background: "transparent",
                    color: "var(--text3)",
                    cursor: "pointer",
                    fontSize: ".78rem",
                  }}
                >
                  Sign in with Octopus account →
                </button>
              )}
            </>
          )}

          {step === "pin" && selected && (
            <>
              <button
                onClick={() => {
                  setStep("roles");
                  setPin("");
                  setErr("");
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "none",
                  border: "none",
                  color: "var(--text3)",
                  cursor: "pointer",
                  fontSize: ".8rem",
                  marginBottom: 20,
                }}
              >
                <IoArrowBackOutline size={14} /> Back
              </button>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: `${ROLE_META[selected].color}18`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.4rem",
                  margin: "0 auto 12px",
                }}
              >
                {ROLE_META[selected].emoji}
              </div>
              <p
                style={{
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: 4,
                }}
              >
                {ROLE_META[selected].label}
              </p>
              <p
                style={{
                  fontSize: ".78rem",
                  color: "var(--text3)",
                  marginBottom: 20,
                }}
              >
                Enter your PIN to access documents
              </p>
              <input
                ref={pinRef}
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && tryPin()}
                placeholder="· · · ·"
                maxLength={8}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: `1px solid ${err ? "#f87171" : "var(--glass-border)"}`,
                  background: "rgba(255,255,255,.04)",
                  color: "var(--text)",
                  fontSize: "1.4rem",
                  textAlign: "center",
                  letterSpacing: ".3em",
                  outline: "none",
                }}
              />
              {err && (
                <p
                  style={{ color: "#f87171", fontSize: ".78rem", marginTop: 8 }}
                >
                  {err}
                </p>
              )}
              <button
                onClick={tryPin}
                style={{
                  marginTop: 14,
                  width: "100%",
                  padding: "12px",
                  borderRadius: 12,
                  border: "none",
                  background: "var(--grad-orange)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: ".88rem",
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                }}
              >
                Access Documents
              </button>
            </>
          )}

          {step === "jwt" && (
            <>
              <button
                onClick={() => setStep("roles")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "none",
                  border: "none",
                  color: "var(--text3)",
                  cursor: "pointer",
                  fontSize: ".8rem",
                  marginBottom: 20,
                }}
              >
                <IoArrowBackOutline size={14} /> Back
              </button>
              <p
                style={{
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: 20,
                }}
              >
                Octopus Account Login
              </p>
              {["Email", "Password"].map((l, i) => (
                <div key={l} style={{ marginBottom: 12, textAlign: "left" }}>
                  <label
                    style={{
                      fontSize: ".72rem",
                      color: "var(--text3)",
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    {l}
                  </label>
                  <input
                    type={i === 1 ? "password" : "email"}
                    value={i === 0 ? email : pass}
                    onChange={(e) =>
                      i === 0
                        ? setEmail(e.target.value)
                        : setPass(e.target.value)
                    }
                    onKeyDown={(e) => e.key === "Enter" && tryJwt()}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "1px solid var(--glass-border)",
                      background: "rgba(255,255,255,.04)",
                      color: "var(--text)",
                      fontSize: ".9rem",
                      outline: "none",
                    }}
                  />
                </div>
              ))}
              {err && (
                <p
                  style={{
                    color: "#f87171",
                    fontSize: ".78rem",
                    marginBottom: 8,
                  }}
                >
                  {err}
                </p>
              )}
              <button
                onClick={tryJwt}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: 12,
                  border: "none",
                  background: "var(--grad-orange)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: ".88rem",
                  cursor: "pointer",
                  opacity: loading ? 0.6 : 1,
                  marginTop: 4,
                }}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </>
          )}
        </div>

        <p style={{ fontSize: ".72rem", color: "var(--text3)", marginTop: 20 }}>
          🔒 Secure · Confidential · Zizian (Pty) Ltd internal use only
        </p>
      </div>
    </div>
  );
}

// ── Markdown viewer ───────────────────────────────────────────────────────────
function MarkdownViewer({ content }) {
  return (
    <div className="md-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

// ── PDF viewer ────────────────────────────────────────────────────────────────
function PdfViewer({ url }) {
  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 180px)",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid var(--border)",
      }}
    >
      <iframe
        src={url}
        style={{ width: "100%", height: "100%", border: "none" }}
        title="PDF Viewer"
      />
    </div>
  );
}

// ── Doc detail panel ──────────────────────────────────────────────────────────
function DocPanel({ doc, onClose, role }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [toc, setToc] = useState([]);
  const contentRef = useRef(null);

  // Extract headings for TOC
  useEffect(() => {
    if (!doc) return;
    const headings = [];
    doc.content.split("\n").forEach((line, i) => {
      const m2 = line.match(/^## (.+)/);
      const m3 = line.match(/^### (.+)/);
      if (m2) headings.push({ level: 2, text: m2[1].trim(), key: i });
      if (m3) headings.push({ level: 3, text: m3[1].trim(), key: i });
    });
    setToc(headings);
  }, [doc]);

  if (!doc) return null;
  const folder = FOLDERS[doc.folder] || {
    color: "#94a3b8",
    icon: "📄",
    label: doc.folder,
  };

  const scrollToHeading = (text) => {
    if (!contentRef.current) return;
    const elems = contentRef.current.querySelectorAll("h2,h3");
    for (const el of elems) {
      if (el.textContent.trim().toLowerCase() === text.toLowerCase()) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        break;
      }
    }
  };

  return (
    <div
      style={{
        position: fullscreen ? "fixed" : "relative",
        inset: fullscreen ? 0 : "auto",
        zIndex: fullscreen ? 60 : 1,
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
        height: fullscreen ? "100vh" : "100%",
      }}
    >
      {/* Doc toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
          background: "rgba(8,8,15,.95)",
          backdropFilter: "blur(20px)",
          flexShrink: 0,
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            color: "var(--text3)",
            cursor: "pointer",
            fontSize: ".78rem",
            padding: "4px 8px",
            borderRadius: 8,
          }}
        >
          <IoArrowBackOutline size={14} />{" "}
          <span className="hidden md:inline">Back</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>{folder.icon}</span>
            <p
              style={{
                fontWeight: 700,
                fontSize: ".9rem",
                color: "var(--text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {doc.title}
            </p>
            <span
              style={{
                fontSize: ".68rem",
                padding: "2px 8px",
                borderRadius: 20,
                background: `${folder.color}18`,
                color: folder.color,
                border: `1px solid ${folder.color}25`,
                flexShrink: 0,
              }}
            >
              {folder.label}
            </span>
          </div>
          <p
            style={{ fontSize: ".72rem", color: "var(--text3)", marginTop: 1 }}
          >
            {readTime(doc.content)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button
            title="Download .md"
            onClick={() => downloadMd(doc)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--glass)",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: ".75rem",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <IoDownloadOutline size={13} />{" "}
            <span style={{ display: "none" }} className="md:block">
              .md
            </span>
          </button>
          <button
            title="Print"
            onClick={printDoc}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--glass)",
              color: "var(--text3)",
              cursor: "pointer",
            }}
          >
            <IoPrintOutline size={13} />
          </button>
          <button
            title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={() => setFullscreen(!fullscreen)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--glass)",
              color: "var(--text3)",
              cursor: "pointer",
            }}
          >
            {fullscreen ? (
              <IoContractOutline size={13} />
            ) : (
              <IoExpandOutline size={13} />
            )}
          </button>
        </div>
      </div>

      {/* Two-column: TOC + content */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* TOC sidebar */}
        {toc.length > 0 && (
          <div
            style={{
              width: 220,
              flexShrink: 0,
              borderRight: "1px solid var(--border)",
              overflowY: "auto",
              padding: "16px 12px",
              background: "rgba(8,8,15,.5)",
            }}
            className="hidden lg:block"
          >
            <p
              style={{
                fontSize: ".65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".12em",
                color: "var(--text3)",
                padding: "0 8px",
                marginBottom: 8,
              }}
            >
              Contents
            </p>
            {toc.map((h, i) => (
              <button
                key={i}
                onClick={() => scrollToHeading(h.text)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: h.level === 2 ? "6px 8px" : "4px 8px 4px 20px",
                  borderRadius: 8,
                  background: "none",
                  border: "none",
                  color: "var(--text3)",
                  cursor: "pointer",
                  fontSize: h.level === 2 ? ".78rem" : ".73rem",
                  lineHeight: 1.4,
                  transition: "all .15s",
                  display: "block",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--text)";
                  e.currentTarget.style.background = "rgba(255,255,255,.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "var(--text3)";
                  e.currentTarget.style.background = "none";
                }}
              >
                {h.text}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div
          ref={contentRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "36px 48px",
            maxWidth: 860,
          }}
          className="print:p-8"
        >
          <MarkdownViewer content={doc.content} />
          <div style={{ height: 80 }} />
        </div>
      </div>
    </div>
  );
}

// ── Doc card ──────────────────────────────────────────────────────────────────
function DocCard({ doc, view, onClick }) {
  const folder = FOLDERS[doc.folder] || {
    color: "#94a3b8",
    icon: "📄",
    label: doc.folder,
  };
  if (view === "list") {
    return (
      <button
        onClick={onClick}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 16px",
          borderRadius: 12,
          border: "1px solid var(--border)",
          background: "var(--glass)",
          cursor: "pointer",
          textAlign: "left",
          transition: "all .15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,.06)";
          e.currentTarget.style.borderColor = "rgba(255,255,255,.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "var(--glass)";
          e.currentTarget.style.borderColor = "var(--border)";
        }}
      >
        <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{folder.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontWeight: 600,
              fontSize: ".88rem",
              color: "var(--text)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {doc.title}
          </p>
          <p
            style={{ fontSize: ".72rem", color: "var(--text3)", marginTop: 1 }}
          >
            {doc.filename}
          </p>
        </div>
        <span
          style={{
            fontSize: ".68rem",
            padding: "2px 8px",
            borderRadius: 20,
            background: `${folder.color}18`,
            color: folder.color,
            border: `1px solid ${folder.color}25`,
            flexShrink: 0,
          }}
        >
          {folder.label}
        </span>
        <span
          style={{ fontSize: ".7rem", color: "var(--text3)", flexShrink: 0 }}
        >
          {Math.round(doc.size / 1024)}KB
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 18,
        borderRadius: 16,
        border: "1px solid var(--border)",
        background: "var(--glass)",
        cursor: "pointer",
        textAlign: "left",
        transition: "all .2s",
        width: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,.06)";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = folder.color + "40";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--glass)";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.borderColor = "var(--border)";
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: "1.6rem" }}>{folder.icon}</span>
        <span
          style={{
            fontSize: ".65rem",
            padding: "2px 7px",
            borderRadius: 20,
            background: `${folder.color}18`,
            color: folder.color,
            border: `1px solid ${folder.color}25`,
          }}
        >
          {folder.label}
        </span>
      </div>
      <div>
        <p
          style={{
            fontWeight: 700,
            fontSize: ".88rem",
            color: "var(--text)",
            lineHeight: 1.4,
            marginBottom: 4,
          }}
        >
          {doc.title}
        </p>
        <p style={{ fontSize: ".72rem", color: "var(--text3)" }}>
          {doc.filename}
        </p>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "auto",
        }}
      >
        <span style={{ fontSize: ".7rem", color: "var(--text3)" }}>
          {doc.lines} lines · {Math.round(doc.size / 1024)}KB
        </span>
      </div>
    </button>
  );
}

// ── Edit panel ────────────────────────────────────────────────────────────────
function EditPanel({ doc, onClose, onSave }) {
  const [content, setContent] = useState(doc.content);
  const [preview, setPreview] = useState(false);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
          background: "rgba(8,8,15,.95)",
          backdropFilter: "blur(20px)",
          flexShrink: 0,
        }}
      >
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            color: "var(--text3)",
            cursor: "pointer",
            fontSize: ".8rem",
          }}
        >
          <IoArrowBackOutline size={14} /> Cancel
        </button>
        <p
          style={{
            flex: 1,
            fontWeight: 700,
            fontSize: ".88rem",
            color: "var(--text)",
          }}
        >
          Editing: {doc.title}
        </p>
        <button
          onClick={() => setPreview(!preview)}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid var(--glass-border)",
            background: "var(--glass)",
            color: "var(--text2)",
            cursor: "pointer",
            fontSize: ".75rem",
          }}
        >
          {preview ? "Edit" : "Preview"}
        </button>
        <button
          onClick={() => onSave(content)}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: "none",
            background: "var(--grad-orange)",
            color: "#fff",
            cursor: "pointer",
            fontSize: ".75rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <IoCheckmarkOutline size={12} /> Save
        </button>
      </div>
      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        {preview ? (
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "36px 48px",
              maxWidth: 860,
            }}
          >
            <div className="md-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MD_COMPONENTS}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={{
              flex: 1,
              background: "var(--surface)",
              color: "var(--text)",
              border: "none",
              outline: "none",
              padding: "24px 32px",
              fontSize: ".85rem",
              fontFamily: "'Cascadia Code','Fira Code',monospace",
              lineHeight: 1.7,
              resize: "none",
            }}
            spellCheck={false}
          />
        )}
      </div>
    </div>
  );
}

// ── Upload panel ──────────────────────────────────────────────────────────────
function UploadPanel({ open, onClose, onAdded, role }) {
  const [title, setTitle] = useState("");
  const [folder, setFolder] = useState("guides");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("write"); // write | upload
  const fileRef = useRef(null);

  const submit = () => {
    if (!title.trim()) return alert("Enter a title");
    let finalContent = content;
    if (mode === "upload" && file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onAdded({
          title,
          folder,
          content: e.target.result,
          filename: file.name,
        });
        reset();
        onClose();
      };
      reader.readAsText(file);
      return;
    }
    if (!finalContent.trim()) return alert("Enter content or upload a file");
    const filename =
      title
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "") + ".md";
    onAdded({ title, folder, content: finalContent, filename });
    reset();
    onClose();
  };

  const reset = () => {
    setTitle("");
    setContent("");
    setFile(null);
    setFolder("guides");
  };

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex" }}>
      <div
        style={{
          flex: 1,
          background: "rgba(0,0,0,.6)",
          backdropFilter: "blur(4px)",
        }}
        onClick={onClose}
      />
      <div
        style={{
          width: "min(600px,100%)",
          background: "var(--surface)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <p style={{ fontWeight: 700, color: "var(--text)" }}>Add Document</p>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text3)",
              cursor: "pointer",
            }}
          >
            <IoCloseOutline size={20} />
          </button>
        </div>
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            flex: 1,
          }}
        >
          {/* Mode toggle */}
          <div
            style={{
              display: "flex",
              gap: 4,
              background: "var(--surface2)",
              padding: 4,
              borderRadius: 12,
            }}
          >
            {[
              ["write", "✏️ Write markdown"],
              ["upload", "📁 Upload .md file"],
            ].map(([m, l]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontSize: ".78rem",
                  fontWeight: 600,
                  background: mode === m ? "var(--glass)" : "transparent",
                  color: mode === m ? "var(--text)" : "var(--text3)",
                  transition: "all .2s",
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {/* Title */}
          <div>
            <label
              style={{
                fontSize: ".72rem",
                textTransform: "uppercase",
                letterSpacing: ".1em",
                color: "var(--text3)",
                display: "block",
                marginBottom: 6,
              }}
            >
              Document Title *
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q2 Sales Strategy"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "rgba(255,255,255,.04)",
                color: "var(--text)",
                fontSize: ".9rem",
                outline: "none",
              }}
            />
          </div>

          {/* Folder */}
          <div>
            <label
              style={{
                fontSize: ".72rem",
                textTransform: "uppercase",
                letterSpacing: ".1em",
                color: "var(--text3)",
                display: "block",
                marginBottom: 6,
              }}
            >
              Category
            </label>
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                fontSize: ".9rem",
                outline: "none",
              }}
            >
              {Object.entries(FOLDERS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.icon} {v.label}
                </option>
              ))}
            </select>
          </div>

          {/* Content */}
          {mode === "write" ? (
            <div style={{ flex: 1 }}>
              <label
                style={{
                  fontSize: ".72rem",
                  textTransform: "uppercase",
                  letterSpacing: ".1em",
                  color: "var(--text3)",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Markdown Content *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  "# Document Title\n\n## Section\n\nYour content here…"
                }
                style={{
                  width: "100%",
                  height: 280,
                  padding: "12px 14px",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                  background: "rgba(255,255,255,.04)",
                  color: "var(--text)",
                  fontSize: ".82rem",
                  fontFamily: "'Cascadia Code','Fira Code',monospace",
                  lineHeight: 1.7,
                  outline: "none",
                  resize: "vertical",
                }}
              />
            </div>
          ) : (
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: "2px dashed var(--border)",
                borderRadius: 12,
                padding: 40,
                textAlign: "center",
                cursor: "pointer",
                transition: "all .2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "var(--orange)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "var(--border)")
              }
            >
              <input
                ref={fileRef}
                type="file"
                accept=".md,.txt,.markdown"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files[0])}
              />
              {file ? (
                <>
                  <p style={{ fontSize: "1.5rem", marginBottom: 8 }}>📄</p>
                  <p style={{ color: "var(--text)", fontWeight: 600 }}>
                    {file.name}
                  </p>
                  <p
                    style={{
                      fontSize: ".75rem",
                      color: "var(--text3)",
                      marginTop: 4,
                    }}
                  >
                    {Math.round(file.size / 1024)}KB
                  </p>
                </>
              ) : (
                <>
                  <IoCloudUploadOutline
                    size={32}
                    style={{ color: "var(--text3)", margin: "0 auto 12px" }}
                  />
                  <p style={{ color: "var(--text2)", fontSize: ".88rem" }}>
                    Click to select a .md file
                  </p>
                  <p
                    style={{
                      fontSize: ".75rem",
                      color: "var(--text3)",
                      marginTop: 4,
                    }}
                  >
                    Markdown files only
                  </p>
                </>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "11px",
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "transparent",
                color: "var(--text2)",
                cursor: "pointer",
                fontSize: ".85rem",
              }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              style={{
                flex: 2,
                padding: "11px",
                borderRadius: 10,
                border: "none",
                background: "var(--grad-orange)",
                color: "#fff",
                cursor: "pointer",
                fontSize: ".85rem",
                fontWeight: 700,
              }}
            >
              Add Document
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Socket.io hook ────────────────────────────────────────────────────────────
function useKbSocket(session, setDocs, setActiveDoc, activeId) {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!session?.token && !session) return;

    // Only attempt socket connection if we have a JWT token
    // PIN-only logins fall back to embedded JSON (already loaded as initial state)
    const hasToken = !!session?.token;
    const socket = io(GW_URL, {
      auth: { token: session?.token || "kb-guest" },
      transports: ["websocket", "polling"],
      reconnection: hasToken, // don't retry on PIN-only sessions
      reconnectionAttempts: hasToken ? 5 : 1,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      // Join role room so we receive broadcasts for our role
      socket.emit("joinKbRole", { kbRole: session?.role || "ops" });
      // Fetch all docs for this role
      socket.emit("fetchKbDocs", { kbRole: session?.role || "ops" });
    });

    // Full list received
    socket.on("kbDocsFetched", ({ docs }) => {
      setDocs(docs || []);
    });

    // Real-time: new doc created by anyone
    socket.on("kbDocCreated", ({ doc }) => {
      setDocs((prev) =>
        prev.find((d) => d.id === doc.id) ? prev : [doc, ...prev],
      );
    });

    // Real-time: doc edited by anyone
    socket.on("kbDocUpdated", ({ doc }) => {
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? doc : d)));
      // If currently viewing this doc, update it live
      if (activeId === doc.id) setActiveDoc(doc);
    });

    // Real-time: doc deleted
    socket.on("kbDocDeleted", ({ docId }) => {
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    });

    socket.on("connect_error", (err) => {
      // Expected when using PIN auth (no JWT) — docs still work from embedded JSON
      console.info("KB socket: using embedded docs (no JWT token)");
      setDocs(DOCS_DATA);
    });

    socket.on("error", () => {
      // Gateway rejected connection — stay on embedded docs
      setDocs(DOCS_DATA);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [session?.token, session?.role]);

  return socketRef;
}

export default function DocsPortal() {
  const [session, setSession] = useState(null);
  const [docs, setDocs] = useState(DOCS_DATA); // starts embedded, replaced by Redis
  const [activeId, setActiveId] = useState(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [folderFilter, setFolderF] = useState("all");
  const [view, setView] = useState("grid");
  const [sidebarOpen, setSidebar] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]);

  const socketRef = useKbSocket(session, setDocs, setActiveDoc, activeId);

  // Restore session from sessionStorage
  useEffect(() => {
    try {
      const s = sessionStorage.getItem("octopus_docs_session");
      if (s) setSession(JSON.parse(s));
    } catch {}
  }, []);

  const login = (sess) => {
    setSession(sess);
    try {
      sessionStorage.setItem("octopus_docs_session", JSON.stringify(sess));
    } catch {}
  };

  const logout = () => {
    setSession(null);
    try {
      sessionStorage.removeItem("octopus_docs_session");
    } catch {}
    setActiveId(null);
  };

  // Role-based folder filtering
  const allowedFolders = useMemo(() => {
    if (!session) return [];
    if (session.role === "director") return Object.keys(FOLDERS);
    return Object.entries(FOLDERS)
      .filter(([, v]) => v.roles.includes(session.role))
      .map(([k]) => k);
  }, [session]);

  // Filter docs by role + search + folder
  const filteredDocs = useMemo(() => {
    return docs.filter((d) => {
      if (!allowedFolders.includes(d.folder)) return false;
      if (folderFilter !== "all" && d.folder !== folderFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          d.title.toLowerCase().includes(q) ||
          d.filename.toLowerCase().includes(q) ||
          d.content.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [docs, allowedFolders, folderFilter, search]);

  // Group by folder for sidebar
  const folderCounts = useMemo(() => {
    const c = {};
    docs.forEach((d) => {
      if (allowedFolders.includes(d.folder))
        c[d.folder] = (c[d.folder] || 0) + 1;
    });
    return c;
  }, [docs, allowedFolders]);

  // activeDoc is kept in sync by setActiveDoc (via useKbSocket on remote edits)
  // Fallback: derive from docs array if state hasn't been set yet
  const resolvedDoc = activeDoc || docs.find((d) => d.id === activeId) || null;

  const saveEdit = (content) => {
    const socket = socketRef.current;
    if (socket?.connected && editing?.id) {
      // Persist to Redis — 'kbDocUpdated' fires live to all viewers
      socket.emit("updateKbDoc", {
        docId: editing.id,
        content,
        savedBy: session?.name,
        note: "Edited via docs portal",
      });
    } else {
      // Local fallback
      setDocs((prev) =>
        prev.map((d) =>
          d.id === editing.id
            ? {
                ...d,
                content,
                lines: content.split("\n").length,
                size: content.length,
              }
            : d,
        ),
      );
    }
    setEditing(null);
  };

  const addDoc = ({ title, folder, content, filename }) => {
    const socket = socketRef.current;
    if (socket?.connected) {
      // Persist to Redis via Socket.io → gateway → KnowledgeBase model
      // Real-time: 'kbDocCreated' will fire on all connected clients
      socket.emit("createKbDoc", {
        title,
        folder,
        content,
        filename,
        kbRole: session?.role,
      });
    } else {
      // Offline fallback — local state only
      const id = `${folder}_${(filename || title).replace(".md", "")}`;
      const f = FOLDERS[folder] || {
        color: "#94a3b8",
        icon: "📄",
        label: folder,
      };
      const newDoc = {
        id,
        folder,
        title,
        filename: filename || title + ".md",
        size: content.length,
        lines: content.split("\n").length,
        content,
        folderLabel: f.label,
        color: f.color,
        icon: f.icon,
      };
      setDocs((prev) => [newDoc, ...prev]);
      setActiveId(id);
    }
  };

  if (!session) return <LoginScreen onLogin={login} />;

  const roleMeta = ROLE_META[session.role] || ROLE_META.ops;

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {editing && (
        <EditPanel
          doc={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
      <UploadPanel
        open={uploading}
        onClose={() => setUploading(false)}
        onAdded={addDoc}
        role={session.role}
      />

      {/* Header */}
      <header
        style={{
          height: 54,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 20px",
          borderBottom: "1px solid var(--border)",
          background: "rgba(8,8,15,.97)",
          backdropFilter: "blur(20px)",
          flexShrink: 0,
          zIndex: 50,
        }}
      >
        <button
          onClick={() => {
            setActiveId(null);
            setSidebar(!sidebarOpen);
          }}
          style={{
            background: "none",
            border: "none",
            color: "var(--text3)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
          className="lg:hidden"
        >
          <IoMenuOutline size={20} />
        </button>
        {activeId && (
          <button
            onClick={() => setActiveId(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: "none",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: ".8rem",
            }}
            className="hidden lg:flex"
          >
            <IoArrowBackOutline size={14} /> All docs
          </button>
        )}
        <p
          style={{
            fontWeight: 900,
            fontSize: "1.1rem",
            textTransform: "uppercase",
            letterSpacing: ".14em",
            background: "var(--grad-orange)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            flexShrink: 0,
          }}
        >
          🐙 Docs
        </p>
        <p
          style={{ fontSize: ".72rem", color: "var(--text3)", flexShrink: 0 }}
          className="hidden md:block"
        >
          Zizian Business Library
        </p>

        {/* Search */}
        <div
          style={{
            flex: 1,
            maxWidth: 400,
            position: "relative",
            marginLeft: "auto",
          }}
        >
          <IoSearchOutline
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text3)",
              pointerEvents: "none",
            }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all documents…"
            style={{
              width: "100%",
              padding: "6px 10px 6px 30px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "rgba(255,255,255,.04)",
              color: "var(--text)",
              fontSize: ".82rem",
              outline: "none",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "var(--text3)",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <IoCloseOutline size={13} />
            </button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setUploading(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 12px",
              borderRadius: 9,
              border: "1px solid var(--glass-border)",
              background: "var(--glass)",
              color: "var(--text2)",
              cursor: "pointer",
              fontSize: ".75rem",
              fontWeight: 600,
            }}
          >
            <IoCloudUploadOutline size={12} />{" "}
            <span className="hidden md:inline">Add Doc</span>
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 20,
              background: `${roleMeta.color}14`,
              border: `1px solid ${roleMeta.color}25`,
            }}
          >
            <span style={{ fontSize: ".85rem" }}>{roleMeta.emoji}</span>
            <span
              style={{
                fontSize: ".72rem",
                fontWeight: 700,
                color: roleMeta.color,
              }}
              className="hidden sm:inline"
            >
              {session.name}
            </span>
          </div>
          <button
            onClick={logout}
            title="Logout"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 10px",
              borderRadius: 9,
              border: "1px solid var(--glass-border)",
              background: "var(--glass)",
              color: "var(--text3)",
              cursor: "pointer",
              fontSize: ".75rem",
            }}
          >
            <IoLogOutOutline size={13} />
          </button>
        </div>
      </header>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Sidebar */}
        <nav
          style={{
            width: 240,
            flexShrink: 0,
            borderRight: "1px solid var(--border)",
            overflowY: "auto",
            padding: "16px 10px",
            background: "rgba(8,8,15,.6)",
            display: sidebarOpen ? "block" : undefined,
            position: "relative",
          }}
          className={`hidden lg:block`}
        >
          {/* All */}
          <button
            onClick={() => {
              setFolderF("all");
              setActiveId(null);
              setSidebar(false);
            }}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 10,
              border: "none",
              background:
                folderFilter === "all" ? "rgba(255,121,0,.12)" : "none",
              color: folderFilter === "all" ? "var(--orange)" : "var(--text2)",
              cursor: "pointer",
              fontSize: ".82rem",
              fontWeight: folderFilter === "all" ? 700 : 400,
              marginBottom: 8,
              textAlign: "left",
            }}
          >
            <IoGridOutline size={14} /> All Documents
            <span
              style={{
                marginLeft: "auto",
                fontSize: ".7rem",
                color: "var(--text3)",
              }}
            >
              {filteredDocs.length}
            </span>
          </button>

          <p
            style={{
              fontSize: ".63rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
              color: "var(--text3)",
              padding: "0 12px",
              marginBottom: 8,
              marginTop: 8,
            }}
          >
            Categories
          </p>
          {Object.entries(FOLDERS)
            .filter(([k]) => allowedFolders.includes(k) && folderCounts[k])
            .map(([k, v]) => (
              <button
                key={k}
                onClick={() => {
                  setFolderF(k);
                  setActiveId(null);
                  setSidebar(false);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "none",
                  background: folderFilter === k ? `${v.color}14` : "none",
                  color: folderFilter === k ? v.color : "var(--text3)",
                  cursor: "pointer",
                  fontSize: ".8rem",
                  fontWeight: folderFilter === k ? 700 : 400,
                  textAlign: "left",
                  transition: "all .15s",
                }}
                onMouseEnter={(e) => {
                  if (folderFilter !== k) {
                    e.currentTarget.style.background = "rgba(255,255,255,.04)";
                    e.currentTarget.style.color = "var(--text2)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (folderFilter !== k) {
                    e.currentTarget.style.background = "none";
                    e.currentTarget.style.color = "var(--text3)";
                  }
                }}
              >
                <span>{v.icon}</span> {v.label}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: ".7rem",
                    color: "var(--text3)",
                  }}
                >
                  {folderCounts[k] || 0}
                </span>
              </button>
            ))}
        </nav>

        {/* Main */}
        <main
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {resolvedDoc ? (
            <DocPanel
              doc={resolvedDoc}
              onClose={() => setActiveId(null)}
              role={session.role}
            />
          ) : (
            <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
              {/* Toolbar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 20,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <p
                    style={{
                      fontWeight: 800,
                      fontSize: "1.05rem",
                      color: "var(--text)",
                      textTransform: "uppercase",
                      letterSpacing: ".05em",
                    }}
                  >
                    {folderFilter === "all"
                      ? "All Documents"
                      : FOLDERS[folderFilter]?.label || folderFilter}
                  </p>
                  <p
                    style={{
                      fontSize: ".72rem",
                      color: "var(--text3)",
                      marginTop: 1,
                    }}
                  >
                    {filteredDocs.length} document
                    {filteredDocs.length !== 1 ? "s" : ""}
                    {search ? ` matching "${search}"` : ""}
                  </p>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                  {[
                    ["grid", IoGridOutline],
                    ["list", IoListOutline],
                  ].map(([v, Icon]) => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                        background:
                          view === v ? "rgba(255,121,0,.12)" : "var(--glass)",
                        color: view === v ? "var(--orange)" : "var(--text3)",
                        cursor: "pointer",
                      }}
                    >
                      <Icon size={14} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid / List */}
              {filteredDocs.length === 0 ? (
                <div style={{ textAlign: "center", padding: "80px 20px" }}>
                  <IoDocumentOutline
                    size={48}
                    style={{
                      color: "var(--text3)",
                      margin: "0 auto 16px",
                      display: "block",
                    }}
                  />
                  <p style={{ color: "var(--text2)", fontWeight: 600 }}>
                    {search
                      ? `No documents match "${search}"`
                      : "No documents in this category"}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      style={{
                        marginTop: 12,
                        padding: "8px 16px",
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                        background: "var(--glass)",
                        color: "var(--text3)",
                        cursor: "pointer",
                        fontSize: ".8rem",
                      }}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : view === "grid" ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))",
                    gap: 14,
                  }}
                >
                  {filteredDocs.map((doc) => (
                    <DocCard
                      key={doc.id}
                      doc={doc}
                      view="grid"
                      onClick={() => setActiveId(doc.id)}
                    />
                  ))}
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {filteredDocs.map((doc) => (
                    <DocCard
                      key={doc.id}
                      doc={doc}
                      view="list"
                      onClick={() => setActiveId(doc.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
